import type { ChewNotification } from "./notification-center";

export const systemNotificationOpenedMessage = "CHEW_NOTIFICATION_OPENED";
export const launchedNotificationParameter = "chewNotification";

export interface SystemNotificationOpenMessage {
  type: typeof systemNotificationOpenedMessage;
  id: string;
  href: string;
}

function shortRepository(repository: string): string {
  return repository.split("/").filter(Boolean).at(-1) ?? repository;
}

export function systemNotificationPresentation(item: ChewNotification): {
  title: string;
  options: NotificationOptions;
} {
  return {
    title: item.kind === "review-requested"
      ? `${shortRepository(item.repository)}#${item.pullNumber} · review requested`
      : item.detail,
    options: {
      body: item.kind === "review-requested" ? `${item.title} — ${item.detail}` : item.title,
      badge: "/icons/chew-192.png",
      data: { id: item.id, href: item.href },
      icon: "/icons/chew-192.png",
      tag: item.id,
    },
  };
}

/**
 * Installed PWAs need a persistent notification owned by their service worker. The Notification
 * constructor remains as a fallback for browsers that expose notifications without service workers.
 */
export async function publishSystemNotification(
  item: ChewNotification,
  onFallbackClick: () => void,
): Promise<boolean> {
  const { title, options } = systemNotificationPresentation(item);

  if ("serviceWorker" in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification(title, options);
      return true;
    } catch {
      // A broken or not-yet-active registration should not disable alerts in browsers that still
      // support page-owned notifications.
    }
  }

  try {
    const notification = new Notification(title, options);
    notification.onclick = () => {
      onFallbackClick();
      notification.close();
    };
    return true;
  } catch {
    return false;
  }
}

export function isSystemNotificationOpenMessage(value: unknown): value is SystemNotificationOpenMessage {
  if (!value || typeof value !== "object") return false;
  const message = value as Partial<SystemNotificationOpenMessage>;
  return message.type === systemNotificationOpenedMessage
    && typeof message.id === "string"
    && typeof message.href === "string";
}

export function takeLaunchedNotification(location = window.location): string | undefined {
  const url = new URL(location.href);
  const id = url.searchParams.get(launchedNotificationParameter) ?? undefined;
  if (!id) return undefined;
  url.searchParams.delete(launchedNotificationParameter);
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  return id;
}
